import { useMemo, useState } from "react";

import CinemaVideoModal from "@/components/CinemaVideoModal";
import ReviewCard from "@/components/marketing/ReviewCard";
import { TESTIMONIALS } from "@/lib/constants/marketing-items";
import { cn } from "@/lib/utils";
import { splitIntoColumns } from "@/lib/utils/marketing";

import type { Testimonial } from "@/lib/constants/marketing-items";

interface ScrollingColumnProps {
  testimonials: Testimonial[];
  direction: "up" | "down";
  speed?: number;
  onPlayVideo: (url: string) => void;
}

function ScrollingColumn({ testimonials, direction, speed = 50, onPlayVideo }: ScrollingColumnProps) {
  const doubled = [...testimonials, ...testimonials];

  return (
    <div className="relative flex-1 max-w-78 min-w-78 h-full overflow-hidden">
      <div
        className={cn("flex flex-col gap-4", direction === "down" ? "animate-scroll-down" : "animate-scroll-up")}
        style={{ ["--scroll-duration" as string]: `${speed}s` }}
      >
        {doubled.map((t, i) => (
          <ReviewCard key={`${t.business ?? t.author ?? i}-${i}`} testimonial={t} onPlayVideo={onPlayVideo} />
        ))}
      </div>
    </div>
  );
}

function RatingsBadge({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 backdrop-blur-sm border border-ash-400/50 rounded-full py-1 pl-1 pr-3 w-fit",
        className
      )}
    >
      <div className="w-[21px] h-[21px] rounded-full flex items-center justify-center bg-honey-500 text-white text-xs">
        ★
      </div>
      <span className="text-xs font-bold uppercase tracking-[1px] text-ash-600">
        Trusted by 1500+ home service pros
      </span>
    </div>
  );
}

function StarRating({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col lg:flex-row items-center gap-1 lg:gap-3", className)}>
      <span className="text-honey-400 text-xl tracking-[2px]">★★★★★</span>
      <span className="text-ash-900 lg:text-ash-600 lg:text-base text-center">
        <span className="font-bold">Rated 5/5</span>
        <span className="text-clay-600 lg:text-ash-600"> on the app store</span>
      </span>
    </div>
  );
}

function ReviewsCta({ className }: { className?: string }) {
  return (
    <a
      href="https://www.google.com/maps/place/Rebolt"
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex items-center justify-center h-12 px-6 rounded-full bg-white border border-ash-300 text-ash-700 text-base font-semibold tracking-[-0.32px] hover:bg-ash-100 transition-colors",
        className
      )}
    >
      Read all 12,300+ reviews
    </a>
  );
}

function FadeOverlay({ position, height = "h-32" }: { position: "top" | "bottom"; height?: string }) {
  return (
    <div
      className={cn(
        "absolute left-0 right-0 z-10 pointer-events-none",
        height,
        position === "top"
          ? "top-0 bg-linear-to-b from-clay-200 via-clay-200/80 to-transparent"
          : "bottom-0 bg-linear-to-t from-clay-200 via-clay-200/80 to-transparent"
      )}
    />
  );
}

export default function ReviewsSection() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  const columns = useMemo(() => splitIntoColumns(TESTIMONIALS, 3), []);
  const doubled = useMemo(() => [...TESTIMONIALS, ...TESTIMONIALS], []);

  const handlePlayVideo = (url: string) => {
    setVideoUrl(url);
    setVideoOpen(true);
  };

  return (
    <section className="bg-clay-200 w-full">
      <CinemaVideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={videoUrl} title="Customer Review" />

      {/* desktop layout */}
      <div className="hidden lg:block max-w-screen-3xl mx-auto px-0 pl-16 xl:pl-28">
        <div className="flex flex-row gap-10 items-start">
          <div className="flex flex-col gap-8 shrink-0 lg:max-w-lg py-24">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-6">
                <RatingsBadge />
                <h2 className="font-clash font-medium text-4xl lg:text-6xl leading-none tracking-[-1.2px] text-ash-900">
                  We let customers do the talking.
                </h2>
              </div>
              <StarRating />
            </div>
            {/* <ReviewsCta className="self-start" /> */}
          </div>

          <div className="relative flex-1 h-[972px] overflow-hidden">
            <FadeOverlay position="top" />
            <FadeOverlay position="bottom" />
            <div className="flex gap-4 h-full">
              <ScrollingColumn testimonials={columns[0]} direction="down" speed={45} onPlayVideo={handlePlayVideo} />
              <ScrollingColumn testimonials={columns[1]} direction="up" speed={50} onPlayVideo={handlePlayVideo} />
              <ScrollingColumn testimonials={columns[2]} direction="down" speed={55} onPlayVideo={handlePlayVideo} />
            </div>
          </div>
        </div>
      </div>

      {/* mobile layout */}
      <div className="lg:hidden flex flex-col items-center gap-3.5 px-5 pt-12 pb-8">
        <div className="flex flex-col items-center gap-3 w-full">
          <div className="flex flex-col items-center gap-3">
            <RatingsBadge className="pr-2" />
            <h2 className="font-clash font-medium text-4xl leading-[1.1] tracking-[-0.64px] text-ash-900 text-center">
              We let our customers
              <br />
              do the talking.
            </h2>
          </div>
          <StarRating />

          <div className="relative w-full h-[760px] overflow-hidden">
            <FadeOverlay position="top" height="h-16" />
            <FadeOverlay position="bottom" height="h-16" />
            <div
              className="flex flex-col gap-4 animate-scroll-down"
              style={{ ["--scroll-duration" as string]: "100s" }}
            >
              {doubled.map((t, i) => (
                <ReviewCard key={`${t.business ?? t.author ?? i}-${i}`} testimonial={t} onPlayVideo={handlePlayVideo} />
              ))}
            </div>
          </div>
        </div>

        {/* <ReviewsCta /> */}
      </div>
    </section>
  );
}
